import { cx } from "@emotion/css";
import Skeleton from "@mui/material/Skeleton";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OutlinedPrimaryButton } from "src/components/Default/LekUI/Button";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { UserDeckCard } from "src/components/NewComponents/Flashcards/UserDecks/UserDeckCard";
import type { SearchDeck } from "src/hooks/useFlashcards";
import { useGetFolder, useGetFolderItemsInfinite } from "src/hooks/useFolders";
import { FolderEmptyItem } from "./FolderEmptyItem";
import { FolderEmptyListView } from "./FolderEmptyListView";
import { FolderSelectionCheckbox } from "./FolderSelectionCheckbox";
import type { FolderItem, FolderItemsProps } from "./types";

interface FolderDeckItemProps {
	item: FolderItem;
	isSelectMode?: boolean;
	isSelected: boolean;
	toggleSelection: FolderItemsProps["toggleSelection"];
}

const FolderDeckItem = ({
	item,
	isSelectMode,
	isSelected,
	toggleSelection,
}: FolderDeckItemProps) => {
	return (
		<div
			data-test-id="folder-deck-item"
			data-deck-id={item.data?.id}
			onClick={
				isSelectMode
					? () => toggleSelection(item.data, "deck", item.id)
					: undefined
			}
			className={cx(isSelectMode && "cursor-pointer p-relative")}
		>
			{isSelectMode && <FolderSelectionCheckbox isSelected={isSelected} />}
			<UserDeckCard
				// Folder API zwraca surowe dane zestawu (Record), w kształcie SearchDeck
				deck={item.data as unknown as SearchDeck}
				className={cx(
					"mb-0 w-100 min-w-350 folder-deck-card",
					isSelected && "border-color-primary",
					isSelectMode && "pointer-events-none",
				)}
			/>
		</div>
	);
};

export const FolderFlashcardsItems = ({
	folderId,
	searchValue,
	isSelectMode,
	selectedItems,
	toggleSelection,
}: FolderItemsProps) => {
	const { t } = useTranslation();
	const { openModal } = useModals();

	const { data: folder } = useGetFolder(folderId);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useGetFolderItemsInfinite(
			folderId,
			{ take: 10, type: "deck", ...(searchValue ? { name: searchValue } : {}) },
			!!folderId,
		);

	const flatItems = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flatMap((page) => page.data || []);
	}, [data?.pages]);

	const totalCount = data?.pages?.[0]?.totalCount || 0;

	if (isLoading) {
		return (
			<div className="flex flex-col w-100 pb-24 mt-24">
				<div className="folder-items-grid">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton
							key={i}
							variant="rounded"
							height={180}
							sx={{ borderRadius: "24px" }}
							className="mb-0 w-100 min-w-350"
						/>
					))}
				</div>
			</div>
		);
	}

	if (flatItems.length === 0) {
		return (
			<div className="flex flex-col w-100 pb-24 pt-24">
				<FolderEmptyListView
					onClick={() => {
						openModal(ModalsNames.AddFolderContentModal, {
							folderId,
							folderName: folder?.name,
							initialTab: "deck",
						});
					}}
				/>
			</div>
		);
	}

	return (
		<div className="flex flex-col w-100">
			<div className="divider divider--horizontal mb-16" />
			{searchValue && (
				<span className="size-18 mb-16">
					{t("folder.searchResultsFor", 'Wyniki dla "{{query}}"', {
						query: searchValue,
					})}
				</span>
			)}
			<div className="folder-items-grid">
				{flatItems.map((item) => (
					<FolderDeckItem
						key={item.id}
						item={item}
						isSelectMode={isSelectMode}
						isSelected={
							!!isSelectMode &&
							!!selectedItems?.some(
								(i) => i.itemId === item.data?.id && i.itemType === "deck",
							)
						}
						toggleSelection={toggleSelection}
					/>
				))}
				<FolderEmptyItem
					onClick={() => {
						openModal(ModalsNames.AddFolderContentModal, {
							folderId,
							folderName: folder?.name,
							initialTab: "deck",
						});
					}}
				/>
			</div>
			{hasNextPage && (
				<div className="flex justify-center items-center gap-16 mt-16">
					<OutlinedPrimaryButton
						onClick={() => fetchNextPage()}
						disabled={isFetchingNextPage}
					>
						{isFetchingNextPage
							? t("folder.loading", "Ładowanie...")
							: t("folder.loadMore", "Załaduj więcej")}
					</OutlinedPrimaryButton>
					<div className="flex flex-col justify-center size-14 text-black-80">
						<span>{t("folder.showing", "Wyświetlane:")}</span>
						<span>
							{t(
								"folder.showingCountValues",
								"{{loaded}} z {{total}} elementów",
								{
									loaded: flatItems.length,
									total: totalCount,
								},
							)}
						</span>
					</div>
				</div>
			)}
		</div>
	);
};
